import { getClientIdWithIdentifier } from '@nackle/bridge';
import { getUser, signIn, signOut, APP_STORAGE_KEY, INTEGRATION_TYPE_KEY, USER_STORAGE_KEY, CLIENT_ID } from './app-auth';
import { getMiddleware as getRouteMiddleware, routes } from './app-routing.jsx';
// Routing
import { Route, routeHistory } from '@nackle/biw-web-router';
import React, { Suspense, useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider, useSelector } from 'react-redux';
import * as microsoftTeams from '@microsoft/teams-js';
import { Layout } from '@nackle/paper';
import { setLanguageBundle } from '@nackle/intl-tools';
import envConstants from './env-constants';
import { idURL } from './endpoints';
import Header from './components/chrome/header/Header';

// Dux
import AppStore from './app-store';
import Branding from './components/branding/branding.jsx';
import { getCompany } from './company.js';
import bundleService from './services/bundle';
import '@nackle/ecard-editor/dist/ant.css';
import AppCodeView from './components/shared/app-code/AppCode';
import LinkView from './components/shared/link-view/LinkView';
import ChromeLinkView from './components/chrome/link-view/LinkView';
import StatusInfo from './components/shared/status-info/StatusInfo';
import { getHashParameters, _guid, toQueryString } from './utils/app-utils';

const urlParams = new Proxy( new URLSearchParams( window.location.search ), {
    get: ( searchParams, prop ) => searchParams.get( prop ),
} );

switch ( urlParams.theme ? urlParams.theme : null ) {
    case 'dark': {
        require( './styles/partials/darkTheme.less' );
        break;
    }
    case 'hc': {
        require( './styles/partials/hcTheme.less' );
        break;
    }
    default: {
        require( './styles/partials/defaultTheme.less' );
    }
}

switch ( urlParams.source ? urlParams.source.toUpperCase() : null ) {
    case 'CHROME': case 'CHROME-LINK': {
        require( './styles/partials/chrome.less' );
        break;
    }
    case 'WORKPLACE': {
        require( './styles/partials/workplace.less' );
        break;
    }
    default: {
        require( './styles/partials/teams.less' );
    }
}


const { Content } = Layout;

microsoftTeams.initialize();

export default function App( props ) {
    
    const loading = useSelector( state => state.app.loading );
    const [ signingIn, setSigningIn ] = useState( false );
    const [ company, setCompany ] = useState( null );
    const [ loggingOut, setLoggingOut ] = useState( urlParams.loggingOut ? urlParams.loggingOut : false );

    const user = JSON.parse( localStorage.getItem( USER_STORAGE_KEY ) );
    const integrationType = urlParams.source ? urlParams.source.toUpperCase() :
        localStorage.getItem( INTEGRATION_TYPE_KEY ) ? localStorage.getItem( INTEGRATION_TYPE_KEY ) : 'TEAMS';
    const closeLogin = urlParams.closeLogin ? urlParams.closeLogin : false;
    const refreshToken = urlParams.refreshToken ? urlParams.refreshToken : null;

    console.log( company );
    useEffect( () => {
        if ( loggingOut ) {
            return handleSignOut();
        }
        const popUpPhase = urlParams.popUpPhase;
        const { code } = getHashParameters();
        if ( code ) {
            return tokenFetch( code );
        }
        if ( popUpPhase === 'start' ) {
            startSignIn();
        }
        if ( integrationType === 'TEAMS' ) {
            microsoftTeams.appInitialization.notifySuccess();
        }
    }, [] );

    useEffect( () => {
        if ( !signingIn )
        {
            load();
        }
    }, [ signingIn ] );

    const needAppCode = () => {
        const appCode = localStorage.getItem( APP_STORAGE_KEY );
        if ( appCode ) {
            return false;
        }
        return true;
    };

    const load = async () => {
        let user = await getUser( refreshToken );
        if ( user && user.access_token ) {
            await setAdditionalInfo( user );
            if ( integrationType === 'TEAMS' && closeLogin ) {
                microsoftTeams.tasks.submitTask( { user }, envConstants.MICROSOFT_APP_ID );
            }
        }
    };

    const setAdditionalInfo = async ( user ) => {
        const { claims: { locale } } = user;
        const bundleData = await Promise.all( [
            await bundleService.getBundle( 'RecognitionNow', locale ),
            await bundleService.getBundle( 'solution', locale )
        ] );

        let mergedBundle = bundleData[ 0 ].bundles[ locale ];
        for ( let i = 1; i < bundleData.length; i++ ) {
            mergedBundle = {
                ...mergedBundle,
                ...bundleData[ i ].bundles[ locale ]
            };
        }
        setLanguageBundle( locale, mergedBundle ).then( () => {
            getCompany( user.claims.company_id ).then( ( company ) => {
                setCompany( company );
            } );
        } );
    };

    const initiateAuthPopup = async () => {
        setSigningIn( true );
        const width = integrationType === 'CHROME' ? '100%' : 500;
        const height = integrationType === 'CHROME' ? '100%' : 600;

        microsoftTeams.authentication.authenticate( {
            url: `${ window.location.origin }?popUpPhase=start`,
            width: width,
            height: height,
            successCallback: ( result ) => { 
                setSigningIn( false );
            },
            failureCallback: ( error ) => {
                setSigningIn( false );
            }
        } );
    };

    const startSignIn = () => {
        setSigningIn( true );
        const appCode = localStorage.getItem( APP_STORAGE_KEY );
        let queryParams = {
            client_id: getClientIdWithIdentifier( CLIENT_ID, appCode ),
            response_type: 'code',
            response_mode: 'fragment',
            scope: 'openid profile company permissions email recovery address phone offline_access',
            redirect_uri: `${ window.location.origin }`,
            nonce: _guid()
        };
        let authorizeEndpoint = `${ idURL }/auth?${ toQueryString( queryParams ) }`;
        window.location.assign( authorizeEndpoint );
    };

    const tokenFetch = async ( code ) => {
        setSigningIn( true );
        const user = await signIn( code );
        microsoftTeams.authentication.notifySuccess( user );
    };

    const handleSignOut = async () => {
        await signOut();
        microsoftTeams.tasks.submitTask( { loggedOut: true }, envConstants.MICROSOFT_APP_ID );
        return setLoggingOut( false );
    };

    let formattedIntegration = integrationType.charAt( 0 ) + integrationType.slice( 1 ).toLowerCase();
    const parseIndex = formattedIntegration.indexOf( '-' );
    if ( parseIndex != -1 ) {
        formattedIntegration = formattedIntegration.substring( 0, parseIndex );
    }

    return (
        <Branding company={ company }>
            <Layout className="recognize">
                <Content className="main-content">
                    <Route render={ ( route ) => {
                        if ( signingIn ) {
                            return (
                                <StatusInfo integrationType={ integrationType } />
                            );
                        }
                        if ( needAppCode() ) {
                            return (
                                <AppCodeView integrationType={ integrationType }
                                    successCallback={ initiateAuthPopup }
                                    tenantId={ urlParams.tenantId } />
                            );
                        }
                        if ( integrationType === 'CHROME-LINK' ) {
                            return (
                                <ChromeLinkView />
                            );
                        }
                        if ( !user ) {
                            return <LinkView appCodeSignin={ initiateAuthPopup } formattedIntegration={ formattedIntegration } />;
                        }
                        if ( loggingOut ) {
                            return <StatusInfo infoType='logout' integrationType={ integrationType } />;
                        }
                        return (
                            <Suspense fallback="..." >
                                {
                                    user && integrationType === 'CHROME' &&
                                    <Header user={ user } />
                                }
                                {
                                    company &&
                                    route.match &&
                                    React.cloneElement( route.component, { loading: loading, company, integrationType, user } )
                                }
                            </Suspense>
                        );
                    } } />  
                </Content>
            </Layout>
        </Branding>  
    );
}

routeHistory.applyRoutes( routes );
routeHistory.applyMiddleware( getRouteMiddleware( AppStore ) );

const root = ReactDOM.createRoot( document.getElementById( 'app-root' ) );

root.render(
    <Provider store={ AppStore }>
        <App />
    </Provider>,
    
);
